import qs from "qs";
import axios from "@/plugins/axios";
import utils from "@/stores/utils";
import documents from "./submodules/documents";
import invitations from "./submodules/invitations";
import customercommunications from "./submodules/customercommunications";
import customerorders from "./submodules/customerorders";
import providerorders from "./submodules/providerorders";
import invoices from "./submodules/invoices";
import customers from "./submodules/customers";
import partnercontacts from "./submodules/partnercontacts";
import $utils from '@/utils';
import { resolvePermissionCheck } from '@/utils/groupPermissions';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

function canViewProviderCatalogEntries(rootGetters) {
    const perms = rootGetters["session/current_user_permissions"];
    return (
        resolvePermissionCheck(perms, "catalog.view_providercatalog") ||
        resolvePermissionCheck(perms, "core.view_provider") ||
        resolvePermissionCheck(perms, "core.view_product")
    );
}

function buildProviderProductsBulkEditFilters(state, filtered = false) {
    if (filtered) {
        const filters = Object.assign({}, state.provider_products_filters, {
            provider: state.provider.id,
        });
        delete filters.limit;
        delete filters.offset;
        return filters;
    }
    return {
        provider: state.provider.id,
    };
}

const state = {
    provider: null,
    provider_loading: false,
    provider_loading_errors: null,
    can_impersonate: false,
    creating_account: false,
    provider_deleting: false,
    provider_stats: null,

    section: null,
    /** Main area: products | purchases | sales (synced with route query.mode). */
    mode: null,

    receipts: [],
    receipts_count: 0,
    receipts_filters: { limit: 20, offset: 0, ordering: "-receipt_date" },
    receipts_loaded: false,
    receipts_loading: false,
    receipts_loading_errors: null,

    purchaseprices: [],
    purchaseprices_count: 0,
    purchaseprices_filters: { offset: 0, limit: 20 },
    purchaseprices_loaded: false,
    purchaseprices_loading: false,
    purchaseprices_loading_errors: null,

    salesprices: [],
    salesprices_count: 0,
    salesprices_filters: { offset: 0, limit: 20, ordering: "catalog_name,product_name" },
    salesprices_loaded: false,
    salesprices_loading: false,
    salesprices_loading_errors: null,

    provider_products: [],
    provider_products_count: 0,
    provider_products_filters: { offset: 0, limit: 20 },
    provider_products_loading: false,
    provider_products_loading_errors: null,
    selected_provider_products: [],

    importing_products: [],

    creating_sales_catalog: false,
    assignedcatalogs: null,

    catalog_pdfs: [],
    catalog_pdfs_loading: false,
};

const mutations = {

    updateProvider(state, provider) {
        if (state.provider && provider && state.provider.id == provider.id) {
            state.provider = provider;
            return;
        }
        state.provider = provider;
        state.provider_stats = null;

        state.receipts = [];
        state.receipts_count = 0;
        state.receipts_loaded = false;
        state.receipts_loading = false;
        state.receipts_loading_errors = null;

        state.purchaseprices = [];
        state.purchaseprices_count = 0;
        state.purchaseprices_loaded = false;
        state.purchaseprices_loading = false;
        state.purchaseprices_loading_errors = null;

        state.salesprices = [];
        state.salesprices_count = 0;
        state.salesprices_loaded = false;
        state.salesprices_loading = false;
        state.salesprices_loading_errors = null;

        state.provider_products = [];
        state.provider_products_count = 0;
        state.provider_products_loading = false;
        state.selected_provider_products = [];
        state.provider_products_loading_errors = null;

        state.catalog_pdfs = [];
        state.catalog_pdfs_loading = false;

        if (!provider) {
            state.mode = null;
            state.section = null;
            state.receipts_filters = { offset: 0, limit: state.receipts_filters.limit };
            state.purchaseprices_filters = {
                offset: 0,
                limit: state.purchaseprices_filters.limit,
            };
            state.salesprices_filters = {
                offset: 0,
                limit: state.salesprices_filters.limit,
            };
            state.provider_products_filters = {
                offset: 0,
                limit: state.provider_products_filters.limit,
            };
            return;
        }

        state.receipts_filters = {
            ...loadFiltersFromStorage(getStorageKey('provider', 'receipts'), { limit: 20, ordering: "-receipt_date" }),
            provider: provider.id,
            offset: 0,
        };
        const savedPurchasePricesFilters = loadFiltersFromStorage(
            getStorageKey('provider', 'purchaseprices'),
            { limit: 20, ordering: "label" },
        );
        state.purchaseprices_filters = {
            ...savedPurchasePricesFilters,
            provider: provider.id,
            offset: 0,
            include_archived: provider.entity
                ? false
                : (savedPurchasePricesFilters.include_archived ?? false),
        };
        const savedSalesPricesFilters = loadFiltersFromStorage(
            getStorageKey('provider', 'salesprices'),
            { limit: 20, ordering: "catalog_name,product_name" },
        );
        state.salesprices_filters = {
            ...savedSalesPricesFilters,
            offset: 0,
            include_archived: savedSalesPricesFilters.include_archived ?? false,
        };
        const savedProviderProductsFilters = loadFiltersFromStorage(
            getStorageKey('provider', 'providerproducts'),
            { limit: 20, ordering: "name,description" },
        );
        if (savedProviderProductsFilters.ordering === "name") {
            savedProviderProductsFilters.ordering = "name,description";
        }
        state.provider_products_filters = {
            ...savedProviderProductsFilters,
            provider: provider.id,
            offset: 0,
            include_archived: false,
        };
    },

    updateCanImpersonate(state, can_impersonate) {
        state.can_impersonate = can_impersonate;
    },
    updateCreatingAccount(state, creating_account) {
        state.creating_account = creating_account;
    },

    updateReceipts(state, receipts) {
        state.receipts = receipts;
    },
    updateReceiptsCount(state, count) {
        state.receipts_count = count;
    },
    updateReceiptsFilters(state, filters) {
        state.receipts_filters = filters;
        saveFiltersToStorage(getStorageKey('provider', 'receipts'), filters, ['limit', 'ordering']);
    },

    updateReceiptsLoading(state, loading) {
        state.receipts_loading = loading;
    },
    updateReceiptsLoadingErrors(state, errors) {
        state.receipts_loading_errors = errors;
    },

    updateProviderLoading(state, loading) {
        state.provider_loading = loading;
    },
    updateProviderLoadingErrors(state, errors) {
        state.provider_loading = false;
        state.provider_loading_errors = errors;
    },

    updateProviderDeleting(state, deleting) {
        state.provider_deleting = deleting;
    },

    updatePurchasePrices(state, purchaseprices) {
        state.purchaseprices = purchaseprices;
    },
    updatePurchasePricesFilters(state, purchaseprices_filters) {
        state.purchaseprices_filters = purchaseprices_filters
        saveFiltersToStorage(getStorageKey('provider', 'purchaseprices'), purchaseprices_filters, ['limit', 'ordering']);
    },
    updatePurchasePricesCount(state, purchaseprices_count) {
        state.purchaseprices_count = purchaseprices_count;
    },
    updatePurchasePricesLoading(state, loading) {
        state.purchaseprices_loading = loading;
    },
    updatePurchasePricesLoadingErrors(state, errors) {
        state.purchaseprices_loading_errors = errors;
    },

    updateSalesPrices(state, salesprices) {
        state.salesprices = salesprices;
    },
    updateSalesPricesFilters(state, salesprices_filters) {
        state.salesprices_filters = salesprices_filters;
        saveFiltersToStorage(
            getStorageKey('provider', 'salesprices'),
            salesprices_filters,
            ['limit', 'ordering'],
        );
    },
    updateSalesPricesCount(state, salesprices_count) {
        state.salesprices_count = salesprices_count;
    },
    updateSalesPricesLoaded(state, loaded) {
        state.salesprices_loaded = loaded;
    },
    updateSalesPricesLoading(state, loading) {
        state.salesprices_loading = loading;
    },
    updateSalesPricesLoadingErrors(state, errors) {
        state.salesprices_loading_errors = errors;
    },

    updateProviderProducts(state, provider_products) {
        state.provider_products = provider_products;
    },
    updateProviderProductsFilters(state, provider_products_filters) {
        state.provider_products_filters = provider_products_filters;
        saveFiltersToStorage(getStorageKey('provider', 'providerproducts'), provider_products_filters, ['limit', 'ordering']);
    },
    updateProviderProductsCount(state, provider_products_count) {
        state.provider_products_count = provider_products_count;
    },
    updateProviderProductsLoading(state, loading) {
        state.provider_products_loading = loading;
    },
    updateProviderProductsLoadingErrors(state, errors) {
        state.provider_products_loading_errors = errors;
    },
    updateSelectedProviderProducts(state, selected_provider_products) {
        state.selected_provider_products = selected_provider_products;
    },

    updateProviderStats(state, provider_stats) {
        state.provider_stats = provider_stats;
    },
    updateSection(state, section) {
        state.section = section;
    },
    updateMode(state, mode) {
        state.mode = mode;
    },
    addImportingProducts(state, products) {
        state.importing_products = state.importing_products.concat(products);
    },
    removeImportingProducts(state, products) {
        state.importing_products = state.importing_products.filter((product) => {
            return !products.includes(product);
        });
    },

    updateAssignedCatalogs(state, assignedcatalogs) {
        state.assignedcatalogs = assignedcatalogs;
    },

    updateCatalogPdfs(state, catalog_pdfs) {
        state.catalog_pdfs = catalog_pdfs;
    },
    updateCatalogPdfsLoading(state, loading) {
        state.catalog_pdfs_loading = loading;
    },
};

const actions = {
    exportToCSV({ commit, dispatch, state, rootGetters }, params) {
        if (!canViewProviderCatalogEntries(rootGetters)) {
            return;
        }
        let filters = {};
        if (params && params.filtered) {
            filters = Object.assign({}, state.purchaseprices_filters);
            filters.offset = null;
        }
        filters.format = "csv";
        filters.limit = CSV_EXPORT_LIMIT;
        filters.provider = state.provider.id;
        filters.catalog_type = state.provider?.entity ? "sell" : "buy";
        if (state.provider?.entity) {
            filters.include_archived = false;
        }
        delete filters.include_archived_providers;
        
        const providerName = state.provider?.name || '';
        
        $utils.downloadFile({
            url: "/api/providercatalogentries/",
            params: filters,
        }, $utils.getFilename('providerCatalogEntries', { provider_name: providerName }));
    },

    async fetchPurchasePrices({ commit, state, rootGetters }, params) {
        if (!state.provider?.id) {
            return;
        }

        if (!canViewProviderCatalogEntries(rootGetters)) {
            commit("updatePurchasePrices", []);
            commit("updatePurchasePricesCount", 0);
            return;
        }

        commit("updatePurchasePricesLoading", true);
        commit("updatePurchasePricesLoadingErrors", null);

        const catalog_type = state.provider.entity ? "sell" : "buy";
        const queryParams = {
            ...state.purchaseprices_filters,
            provider: state.provider.id,
            catalog_type,
        };
        if (state.provider.entity) {
            queryParams.include_archived = false;
        }
        delete queryParams.include_archived_providers;
        try {
            const response = await axios.get(`/api/providercatalogentries/`, {
                params: queryParams,
            });
            commit("updatePurchasePrices", response.data.results);
            commit("updatePurchasePricesCount", response.data.count);
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit("updatePurchasePricesLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updatePurchasePricesLoading", false);
        }
    },

    async fetchSalesPrices({ commit, state, rootGetters }) {
        if (!state.provider?.id) {
            return;
        }

        if (
            !resolvePermissionCheck(
                rootGetters["session/current_user_permissions"],
                "catalog.view_sellcatalog",
            )
        ) {
            commit("updateSalesPrices", []);
            commit("updateSalesPricesCount", 0);
            commit("updateSalesPricesLoaded", true);
            return;
        }

        commit("updateSalesPricesLoading", true);
        commit("updateSalesPricesLoadingErrors", null);

        const queryParams = {
            ...state.salesprices_filters,
            supplier_sales_context: state.provider.id,
        };

        try {
            const response = await axios.get("/api/productcatalogentries/", {
                params: queryParams,
            });
            commit("updateSalesPrices", response.data.results);
            commit("updateSalesPricesCount", response.data.count);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateSalesPricesLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateSalesPricesLoading", false);
            commit("updateSalesPricesLoaded", true);
        }
    },

    async exportSalesPricesAsCSV({ state }, { filters } = {}) {
        if (!state.provider?.id) {
            return;
        }

        const exportFilters = {
            ...(filters || state.salesprices_filters),
            supplier_sales_context: state.provider.id,
            offset: 0,
            limit: CSV_EXPORT_LIMIT,
            format: "csv",
        };

        const providerName = state.provider?.name || "";

        $utils.downloadFile(
            {
                url: "/api/productcatalogentries/",
                params: exportFilters,
            },
            $utils.getFilename("providerSalesPrices", { provider_name: providerName }),
        );
    },

    async patchSalesPriceEntry({ commit, dispatch, state }, { catalogentry }) {
        try {
            const response = await axios.put(
                `/api/catalogentries/${catalogentry.id}/`,
                catalogentry,
            );
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async setSalesPriceEntryActive({ dispatch }, { catalogentry, active }) {
        try {
            await axios.post(`/api/catalogentries/${catalogentry.id}/active/`, {
                active,
            });
            await dispatch("fetchSalesPrices");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async archiveSalesPriceEntry({ dispatch, state }, { instance }) {
        try {
            await axios.post(`/api/catalogentries/${instance.id}/archive/`);
            await dispatch("fetchSalesPrices");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async fetchProviderProducts({ commit, state, rootGetters }) {
        if (!state.provider?.id) {
            return;
        }

        if (
            !resolvePermissionCheck(
                rootGetters["session/current_user_permissions"],
                "core.view_provider",
            )
        ) {
            commit("updateProviderProducts", []);
            commit("updateProviderProductsCount", 0);
            return;
        }

        commit("updateProviderProductsLoading", true);
        commit("updateProviderProductsLoadingErrors", null);
        try {
            const response = await axios.get("/api/providerproducts/", {
                params: { ...state.provider_products_filters, provider: state.provider.id },
            });
            commit("updateProviderProducts", response.data.results);
            commit("updateProviderProductsCount", response.data.count);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateProviderProductsLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateProviderProductsLoading", false);
        }
    },

    getProviderProductsBulkEditFilters({ state }, { filtered = false } = {}) {
        return buildProviderProductsBulkEditFilters(state, filtered);
    },

    async fetchReceipts({ commit, dispatch, state }, params) {

        commit("updateReceiptsLoading", true);
        commit("updateReceiptsLoadingErrors", null);

        try {
            const response = await axios.get("/api/receipts/", {
                params: state.receipts_filters,
            });
            commit("updateReceiptsLoading", false);
            commit("updateReceipts", response.data.results);
            commit("updateReceiptsCount", response.data.count);
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit("updateReceiptsLoading", false);
            commit("updateReceiptsLoadingErrors", error.details);
            throw error;
        }
    },

    async fetchProvider({ commit, dispatch, state }, params) {

        commit("updateProviderLoading", true);
        commit("updateProviderLoadingErrors", null);

        let url = `/api/providers/${state.provider.id}/`;
        try {
            const response = await axios.get(url);
            commit("updateProvider", response.data);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateProviderLoadingErrors", error.details);
            throw error;
        } finally {
            dispatch("fetchProviderStats");
            commit("updateProviderLoading", false);
        }
    },

    async deleteProvider({ commit, dispatch, state }, params) {

        commit("updateProviderDeleting", true);

        const url = `/api/providers/${params.instance.id}/archive/`;
        try {
            const response = await axios.post(url);
            commit("updateProviderDeleting", false);
            return response;
        } catch (xhr_error) {
            commit("updateProviderDeleting", false);
            throw utils.handleError(xhr_error);
        } finally {
            dispatch("session/fetchStats", null, { root: true });
        }
    },


    async fetchProviderStats({ commit, dispatch, state }, params) {
        try {
            const response = await axios.get(`/api/providers/${state.provider.id}/stats/`);
            commit("updateProviderStats", response.data);
        } catch (error) {
            console.error('Error fetching provider stats:', error);
        }
    },


    async updateProductsPrices({ commit, dispatch, state }, prices) {
        try {
            const response = await axios.post(`/api/providers/${state.provider.id}/productprices/`, prices);
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            throw error;
        }
    },


    async checkIfCanImpersonate({ commit, dispatch, state }, params) {
        try {
            let response = await axios.head(`/api/providers/${state.provider.id}/impersonate/`);
            commit("updateCanImpersonate", true);
        } catch (xhr_error) {
            commit("updateCanImpersonate", false);
        }
    },


    async inviteProvider({ commit, dispatch, state }, params) {

        try {
            await dispatch("invitations/saveInvitation", {
                data: {
                    provider: state.provider.id,
                    email_or_safetee_id: params.email_or_safetee_id,
                }
            });
        } finally {

            dispatch("invitations/fetchInvitations");
        }
    },

    async createProviderAccount({ commit, dispatch, state }, params) {
        commit("updateCreatingAccount", true);
        try {
            let response = await axios.post(
                `/api/providers/${state.provider.id}/create_account/`
            );
            commit("updateCreatingAccount", false);
            dispatch("fetchProvider");
            dispatch("checkIfCanImpersonate");
            return response.data;
        } catch (xhr_error) {
            commit("updateCreatingAccount", false);
            throw utils.handleError(xhr_error);
        }
    },

    async cancelProviderInvitation({ commit, dispatch, state }, params) {

        try {
            await dispatch("invitations/deleteInvitation", {
                instance: state.invitations.invitations[0],
            });
        } finally {

            dispatch("invitations/fetchInvitations");
        }
    },

    async archiveCatalogEntry({ commit, dispatch, state }, params) {

        try {
            await axios.post(`/api/providercatalogentries/${params.catalogentry.id}/archive/`);
        } finally {
            dispatch("fetchPurchasePrices");
            dispatch("fetchProviderProducts");
        }
    },

    async archiveProviderProduct({ commit, dispatch, state }, params) {
        try {
            await axios.post(`/api/providerproducts/${params.product.id}/archive/`);
        } finally {
            dispatch("fetchProviderProducts");
        }
    },

    async unarchiveProvider({ commit, dispatch, state }, params) {

        try {
            await axios.post(`/api/providers/${state.provider.id}/unarchive/`);
        } finally {
            dispatch("fetchProvider");
        }
    },

    async createSaleProduct({ commit, dispatch, state }, params) {
        const productIds = [].concat(params.products || []);
        commit("addImportingProducts", productIds);
        try {
            const response = await axios.post(`/api/importproducts/`, {
                products: params.products
            });
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch("fetchPurchasePrices");
            dispatch("fetchProviderProducts");
            commit("removeImportingProducts", params.products);
        }
    },

    async fetchAssignedCatalogs({ commit, state, rootGetters }) {
        if (!state.provider?.id) {
            return;
        }

        if (
            !resolvePermissionCheck(
                rootGetters["session/current_user_permissions"],
                "catalog.view_sellcatalog",
            )
        ) {
            commit("updateAssignedCatalogs", []);
            return;
        }

        try {
            const listParams = { type: "sell", limit: 500 };
            const [assignedResponse, ownedResponse] = await Promise.all([
                axios.get(`/api/catalogs/`, {
                    params: { ...listParams, provider: state.provider.id },
                }),
                axios.get(`/api/catalogs/`, {
                    params: { ...listParams, owned: true },
                }),
            ]);
            const assigned = assignedResponse.data?.results || [];
            const owned = ownedResponse.data?.results || [];
            const seen = new Set();
            const merged = [];
            for (const catalog of assigned) {
                if (catalog?.id != null && !seen.has(catalog.id)) {
                    seen.add(catalog.id);
                    merged.push(catalog);
                }
            }
            for (const catalog of owned) {
                if (catalog?.id != null && !seen.has(catalog.id)) {
                    seen.add(catalog.id);
                    merged.push(catalog);
                }
            }
            commit("updateAssignedCatalogs", merged);
            return assignedResponse;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async fetchCatalogPdfs({ commit, dispatch, state }, params) {
        if (!state.provider || !state.provider.id) {
            return;
        }

        commit("updateCatalogPdfsLoading", true);
        try {
            const response = await axios.get(
                `/api/providers/${state.provider.id}/catalogfiles/`
            );
            commit("updateCatalogPdfs", response.data || []);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateCatalogPdfs", []);
            throw error;
        } finally {
            commit("updateCatalogPdfsLoading", false);
        }
    },

    async init({ commit, dispatch, state }, params) {
        await commit("updateProvider", params?.provider);
        await commit("updateProviderStats", null);
        await commit("updateCanImpersonate", false);
        await commit("updateSection", params?.section || null);
        dispatch("fetchProviderStats");
        if (!params.provider) {
            return;
        }
        await dispatch("documents/init", {
            storageKey: getStorageKey('provider', 'documents'),
            fieldsToSave: ['limit', 'ordering'],
            tags: `provider:${params.provider.id}`,
            create_tags: [`provider:${params.provider.id}`],
        });
        // dispatch("documents/fetchDocuments");

        await dispatch("providerorders/init", {
            storageKey: getStorageKey('provider', 'providerorders'),
            fieldsToSave: ['limit', 'ordering'],
            filters: { seller: params.provider.id, ordering: "-id" }
        });
        // dispatch("providerorders/fetchOrders");

        await dispatch("customerorders/init", {
            storageKey: getStorageKey('provider', 'customerorders'),
            fieldsToSave: ['limit', 'ordering'],
            fetch_stats: true,
            url: `/api/providers/${params.provider.id}/customerorders/`,
            filters: { ordering: "-id"}
        });
        // dispatch("customerorders/fetchOrders");

        await dispatch("invoices/init", {
            storageKey: getStorageKey('provider', 'invoices'),
            fieldsToSave: ['limit', 'ordering'],
            url: "/api/providerinvoices/", filters: { provider: params.provider.id }
        });
        // dispatch("invoices/fetchInvoices");

        await dispatch("salesinvoices/init", {
            storageKey: getStorageKey('provider', 'salesinvoices'),
            fieldsToSave: ['limit', 'ordering'],
            url: "/api/customerinvoices/",
            fetch_stats: true,
            filters: {
                provider: params.provider.id,
                issued: false,
                ordering: "-invoice_date,billed_name",
            },
        });

        await dispatch("contacts/init", {
            url: `/api/providers/${params.provider.id}/contacts/`,
        });
        dispatch("contacts/fetchContacts");

        await dispatch("communications/init", {
            url: `/api/providers/${params.provider.id}/communications/`,
        });

        await dispatch("invitations/init", {
            storageKey: getStorageKey('provider', 'invitations'),
            fieldsToSave: ['limit', 'ordering'],
            filters: { provider: params.provider.id }
        });
        if (!state.provider.entity) {
            dispatch("invitations/fetchInvitations");
        }
        await commit("updateAssignedCatalogs", null);
        dispatch("fetchAssignedCatalogs");
        dispatch("checkIfCanImpersonate");

        await dispatch("buyers/init", {
            storageKey: getStorageKey('provider', 'buyers'),
            fieldsToSave: ['limit', 'ordering'],
            url: `/api/providers/${params.provider.id}/buyers/`
        });
        // dispatch("buyers/fetchCustomers");

        dispatch("fetchCatalogPdfs");

    },

};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    modules: {
        documents: documents({
            storageKey: getStorageKey('provider', 'documents'),
            fieldsToSave: ['limit', 'ordering']
        }),
        invitations: invitations({
            storageKey: getStorageKey('provider', 'invitations'),
            fieldsToSave: ['limit', 'ordering']
        }),
        customerorders: customerorders({
            storageKey: getStorageKey('provider', 'customerorders'),
            fieldsToSave: ['limit', 'ordering']
        }),
        providerorders: providerorders({
            storageKey: getStorageKey('provider', 'providerorders'),
            fieldsToSave: ['limit', 'ordering']
        }),
        invoices: invoices({
            storageKey: getStorageKey('provider', 'invoices'),
            fieldsToSave: ['limit', 'ordering']
        }),
        salesinvoices: invoices({
            storageKey: getStorageKey('provider', 'salesinvoices'),
            fieldsToSave: ['limit', 'ordering']
        }),
        buyers: customers({
            storageKey: getStorageKey('provider', 'buyers'),
            fieldsToSave: ['limit', 'ordering']
        }),
        contacts: partnercontacts({
            storageKey: getStorageKey('provider', 'contacts'),
            fieldsToSave: ['limit', 'ordering'],
            permission: 'core.view_provider',
        }),
        communications: customercommunications({
            fieldsToSave: ['limit'],
            storageKey: getStorageKey('provider', 'communications'),
        }),
    },
};
